import {
  GlobalNavBarProps,
  GlobalNavBarApiResponse,
  HighbondNavigationDependencies,
  HighbondNavigationError,
  BackendApiService,
  ApiPathService,
  AppConfigService,
  WindowRef,
} from "./types";

/**
 * HighbondNavigationService - TypeScript implementation
 *
 * This is a framework-independent TypeScript implementation of the Highbond navigation service,
 * migrated from the Angular version for better type safety and maintainability.
 *
 * Key improvements over Angular version:
 * - Full TypeScript with type safety
 * - No jQuery dependency (removed deprecated synchronous AJAX)
 * - Class-based with dependency injection
 * - Framework-agnostic (can be used in React, Angular, or vanilla JS)
 * - Better error handling
 * - Proper caching strategy
 */
export class HighbondNavigationService {
  private backendApi: BackendApiService;
  private ApiPath: ApiPathService;
  private appConfig: AppConfigService;
  private windowRef: WindowRef;

  /**
   * Constructor with dependency injection
   * @param dependencies - All required dependencies for the service
   */
  constructor(dependencies: HighbondNavigationDependencies) {
    this.backendApi = dependencies.backendApi;
    this.ApiPath = dependencies.ApiPath;
    this.appConfig = dependencies.appConfig;
    this.windowRef = dependencies.windowRef || (window as WindowRef);
  }

  /**
   * Get global navigation bar properties (async)
   *
   * This method fetches the global navigation bar properties from the backend API.
   * Results are cached in AppConfig.highbondNavBarProps to prevent multiple API calls.
   *
   * @returns Promise resolving to GlobalNavBarProps
   * @throws HighbondNavigationError if the API call fails
   *
   * @example
   * const navProps = await service.getGlobalNavProps();
   * console.log(navProps.userFullName);
   */
  async getGlobalNavProps(): Promise<GlobalNavBarProps> {
    try {
      // Return cached value if available
      if (this.appConfig.highbondNavBarProps) {
        return this.appConfig.highbondNavBarProps;
      }

      // Build the redirect URL parameter
      const redirectQueryParam = `?redirect_uri=${this.windowRef.location.href}`;

      // Prepare the HTTP request
      const request = {
        method: "GET",
        url: this.ApiPath.highbond.getGlobalNavBarUrl() + redirectQueryParam,
        headers: {
          Accept: "application/json",
        },
      };

      // Make the API call
      const response: GlobalNavBarApiResponse = await this.backendApi.http(request);

      // Extract the data from the response
      // The API returns { data: { data: { ...actual props } } }
      const highbondNavBarProps: GlobalNavBarProps = response?.data?.data || {};

      // Cache the result in AppConfig to prevent multiple API calls
      this.appConfig.highbondNavBarProps = highbondNavBarProps;

      return highbondNavBarProps;
    } catch (error: any) {
      throw this.handleError("getGlobalNavProps", error);
    }
  }

  /**
   * Get global navigation bar properties (synchronous) - DEPRECATED
   *
   * @deprecated This method uses synchronous AJAX which is deprecated and blocks the UI.
   * Use getGlobalNavProps() instead for async behavior.
   *
   * This method is maintained for backward compatibility but will be removed in future versions.
   * It attempts to return cached data if available, otherwise returns an empty object.
   *
   * Migration guide:
   * - Replace: const data = service.getGlobalNavPropsSync();
   * - With: const data = await service.getGlobalNavProps();
   *
   * @returns GlobalNavBarProps (cached data or empty object)
   */
  getGlobalNavPropsSync(): GlobalNavBarProps {
    console.warn(
      "⚠️ HighbondNavigationService.getGlobalNavPropsSync() is deprecated. " +
        "Use async getGlobalNavProps() instead. Synchronous AJAX is not supported in modern browsers."
    );

    // Return cached value if available
    if (this.appConfig.highbondNavBarProps) {
      return this.appConfig.highbondNavBarProps;
    }

    // Log warning that sync call is not possible
    console.warn(
      "⚠️ No cached navigation props available. Please use async getGlobalNavProps() method. " +
        "Returning empty object for backward compatibility."
    );

    // Return empty object for backward compatibility
    // The calling code should handle this gracefully
    return {};
  }

  /**
   * Clear cached navigation properties
   * Useful for testing or when user context changes
   */
  clearCache(): void {
    if (this.appConfig.highbondNavBarProps) {
      this.appConfig.highbondNavBarProps = undefined;
    }
  }

  /**
   * Check if navigation properties are cached
   * @returns boolean indicating if cache exists
   */
  isCached(): boolean {
    return !!this.appConfig.highbondNavBarProps;
  }

  /**
   * Handle and format errors consistently
   * @private
   */
  private handleError(operation: string, error: any): HighbondNavigationError {
    const errorMessage = error?.message || error?.toString() || "Unknown error occurred";

    // Create a formatted error object
    const formattedError: HighbondNavigationError = {
      message: `HighbondNavigationService.${operation}: ${errorMessage}`,
      originalError: error,
    };

    // Preserve additional error properties if they exist
    if (error?.code) {
      formattedError.code = error.code;
    }
    if (error?.status) {
      formattedError.status = error.status;
    }

    // Log the error for debugging
    console.error(`❌ ${formattedError.message}`, error);

    return formattedError;
  }
}

